import { FC, ReactNode } from "react";
import { useTranslation } from "@vixi/localization";
import { RequestedFeature } from "@vixi/types";
import { UpvoteButton } from "./FeatureListItem";
import {
  useDownvoteFeature,
  useDownvoteFeatureState,
  useUpvoteFeature,
  useUpvoteFeatureState,
} from "@vixi/api";
import { Button } from "../base/button";
import { FeatureAttachment } from "./FeatureAttachment";
import { Dialog, DialogContent } from "../base/dialog";
import { XIcon } from "lucide-react";

interface FeatureDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  feature: RequestedFeature | null;
  actions?: ReactNode;
}

export const FeatureDetailsDialog: FC<FeatureDetailsDialogProps> = ({
  open,
  onOpenChange,
  feature,
  actions,
}) => {
  const { t, tNum } = useTranslation();

  const { mutateAsync: upvoteFeature } = useUpvoteFeature();
  const { mutateAsync: downvoteFeature } = useDownvoteFeature();

  const _upvoteState = useUpvoteFeatureState();
  const _downvoteState = useDownvoteFeatureState();

  const onClickUpvote = async (feature: RequestedFeature) => {
    if (feature.isVoted) {
      downvoteFeature(feature.id);
    } else {
      upvoteFeature(feature.id);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="w-225 max-w-[calc(100%-60px)]">
        <div className="flex items-center justify-between">
          <h3>{feature?.title}</h3>
          <Button
            size="icon"
            variant="text"
            color="foreground"
            aria-label="close"
            onClick={() => onOpenChange(false)}
          >
            <XIcon />
          </Button>
        </div>

        <div>
          <p className="text-foreground-secondary">{feature?.details}</p>

          <div className="mt-2.5">
            {feature && (feature?.attachments.length ?? 0) > 0 ? (
              <>
                <h4>{t("Common.Attachments")}</h4>
                <div className="mt-2 grid grid-cols-1 gap-2.5 sm:grid-cols-3 md:grid-cols-4">
                  {feature.attachments.map((attachment, i) => (
                    <div key={i}>
                      <FeatureAttachment url={attachment} />
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <h5 className="text-foreground-secondary">{t("Common.NoAttachments")}</h5>
            )}
          </div>
        </div>

        <div className="mt-0.5 flex justify-end gap-2">
          {actions ?? (
            <UpvoteButton
              isVoted={feature?.isVoted}
              onClick={() => feature && onClickUpvote(feature)}
            >
              {feature && tNum(feature.votes)}
            </UpvoteButton>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
